import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import Google from 'assets/images/google_play.svg';
import Apple from 'assets/images/app_store.svg';
import LoadingComponent from 'components/LoadingComponent';
import Call4Impact from 'components/Cards/Call4ImpactCard';
import Agenda from 'components/Agenda';
import UsersModal from 'components/UsersModal';
import UserSmallCard from 'components/Cards/UserSmallCard/components';
import SimpleCarousel from 'components/SimpleCarousel';
import { getRecommendedUsers } from 'modules/users/actions';
import { getRecommendedTables } from 'modules/think-seeds/actions';
import { NARRATIVE, MEETING, SPEED_DATING } from 'constants/types';
import NoResultsImg from 'assets/images/think/no_think_chats.svg';
import ThemeTableCard from 'components/Cards/ThemeTableCard';
import { Carousel } from 'react-materialize';
import {
  NarrativesWrapper, PhoneUrl, LoadingWrapper, NoElements,
} from './styles';

function Narratives(props) {
  const {
    resetAll, scheduleSpeedDatings, literals, setBreadcrumb, resetContext,
    narratives, itemsPreferencesFromSerendipity, themeTables, app, history,
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingTables, setIsLoadingTables] = useState(true);
  const [recommendedUsers, setRecommendedUsers] = useState(null);
  const [recommendedTables, setRecommendedTables] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchUsers, setSearchUsers] = useState('');
  const [searchTables, setSearchTables] = useState('');
  const [thereAreUsers, setThereAreUsers] = useState(false);
  const [thereAreTables, setThereAreTables] = useState(false);

  const loadRecommendedUsers = () => {
    getRecommendedUsers(1, searchUsers)
      .then((response) => {
        setRecommendedUsers(response);
        if (response.length) {
          setThereAreUsers(true);
        }
      })
      .catch((error) => {
        console.error(error);
        setRecommendedUsers([]);
      });
  };

  const loadRecommendedTables = () => {
    getRecommendedTables(1, searchTables)
      .then((response) => {
        setRecommendedTables(response);
        if (response.length) {
          setThereAreTables(true);
        }
      })
      .catch((error) => {
        console.error(error);
        setRecommendedTables([]);
      });
  };

  const init = () => {
    const breadcrumb = [
      {
        color: '#ffffff',
        text: literals.title,
      },
    ];

    resetAll();
    setBreadcrumb(breadcrumb.slice());
    resetContext();
  };

  const getTimelineCards = () => {
    const meetings = [];
    const speedDatings = [];

    narratives.forEach((narrative) => {
      narrative.speed_datings.forEach((speedDating) => {
        if (!speedDatings.find(({ id }) => id === speedDating.id) && !moment().isAfter(moment(speedDating.start_date, 'X').format('YYYY-MM-DD'), 'day')) {
          speedDatings.push(speedDating);
        }
      });

      narrative.meetings.forEach((meeting) => {
        if (!meetings.find(({ id }) => id === meeting.id) && !moment().isAfter(moment(meeting.start_date, 'X').format('YYYY-MM-DD'), 'day')) {
          meetings.push(meeting);
        }
      });
    });

    themeTables.forEach((themeTable) => {
      themeTable.speed_datings.forEach((speedDating) => {
        if (!speedDatings.find(({ id }) => id === speedDating.id) && !moment().isAfter(moment(speedDating.start_date, 'X').format('YYYY-MM-DD'), 'day')) {
          speedDatings.push(speedDating);
        }
      });

      themeTable.meetings.forEach((meeting) => {
        if (!meetings.find(({ id }) => id === meeting.id) && !moment().isAfter(moment(meeting.start_date, 'X').format('YYYY-MM-DD'), 'day')) {
          meetings.push(meeting);
        }
      });
    });

    return { meetings, speedDatings };
  };

  // Effects
  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (recommendedUsers !== null && recommendedTables !== null) {
      setIsLoading(false);
    }
  }, [recommendedUsers, recommendedTables]);

  useEffect(() => {
    if (recommendedUsers !== null) {
      setIsLoadingUsers(false);
    }
  }, [recommendedUsers]);

  useEffect(() => {
    if (recommendedTables !== null) {
      setIsLoadingTables(false);
    }
  }, [recommendedTables]);

  // Handler events
  const handleClickUser = (userId) => {
    setSelectedUser(userId);
  };

  const removeSelectedUser = () => {
    setSelectedUser(null);
    loadRecommendedUsers();
  };

  const handleSearchUsers = (event) => {
    const { target: { value } } = event;
    setSearchUsers(value);
  };

  const handleSearchTables = (event) => {
    const { target: { value } } = event;
    setSearchTables(value);
  };

  const handleClickTable = (tableId) => {
    history.push(`/theme-table/${tableId}`);
  };

  useEffect(() => {
    const toSearchUsers = setTimeout(() => {
      loadRecommendedUsers();
      setIsLoadingUsers(true);
    }, 1000);

    return () => {
      clearTimeout(toSearchUsers);
    };
  }, [searchUsers]);

  useEffect(() => {
    const toSearchTables = setTimeout(() => {
      loadRecommendedTables();
      setIsLoadingTables(true);
    }, 1000);

    return () => {
      clearTimeout(toSearchTables);
    };
  }, [searchTables]);

  const openApple = () => {
    window.open('https://apps.apple.com/us/app/ci-call-for-impact/id1377297200');
  };

  const openGoogle = () => {
    window.open('https://play.google.com/store/apps/details?id=com.grupogo.c2i&hl=es_419&gl=US');
  };

  // Render functions
  const printCall4Impact = () => {
    const { speedDatings, meetings } = getTimelineCards();

    const time = moment.unix();
    const finalNarratives = _.filter(narratives, (narrative) => {
      if (narrative.type.id !== 21227760 || (narrative.time_limit !== 0 && narrative.time_limit < time)) {
        return false;
      }

      return true;
    });

    if (!finalNarratives.length) {
      return null;
    }

    finalNarratives.sort((a, b) => {
      const apos = !_.find(itemsPreferencesFromSerendipity, item => item.element_type === NARRATIVE && item.element_id === a.id && !item.enabled);
      const bpos = !_.find(itemsPreferencesFromSerendipity, item => item.element_type === NARRATIVE && item.element_id === b.id && !item.enabled);
      if (apos && !bpos) {
        return -1;
      }
      return 1;
    });

    const meetingsElements = meetings.map(meeting => ({
      type: MEETING,
      data: meeting,
    }));

    const speedDatingsElements = speedDatings.map(speedDating => ({
      type: SPEED_DATING,
      data: speedDating,
    }));

    // Exclude events concluded
    const now = moment().unix();
    const allElements = _.filter(meetingsElements.concat(speedDatingsElements, meetingsElements), (meeting) => {
      const endDate = typeof meeting.data.end_date !== 'undefined' ? meeting.data.end_date : meeting.data.start_date + 3600 * 6;
      return endDate > now;
    });
    allElements.sort((a, b) => {
      return a.data.start_date - b.data.start_date;
    });

    let narrativesElements = null;


    narrativesElements = _.map(finalNarratives, (narrative) => {
      let planId = 0;
      let type = '';

      // buscamos si hay algun speed dating o meeting asociado a esta narrativa
      if (allElements) {
        const plan = allElements.find(element => element.data.element_id === narrative.id);

        if (plan) {
          if (plan.type === SPEED_DATING) {
            planId = plan.data.id;
            type = plan.type;
          }

          if (plan.type === MEETING) {
            planId = plan.data.zoom_data.id;
            type = plan.type;
          }
        }
      }

      return (
        <div key={narrative.id}>
          <Call4Impact
            data={narrative}
            planId={planId}
            type={type}
          />
        </div>
      );
    });

    return (
      <React.Fragment>
        <div className='title_section' dangerouslySetInnerHTML={{ __html: literals.call4ImpactTitle }} />
        <Carousel
          carouselId='Carousel-2'
          options={{
            dist: -20,
            duration: 200,
            fullWidth: false,
            indicators: false,
            noWrap: false,
            numVisible: 6,
            onCycleTo: null,
            padding: -170,
            shift: 0,
          }}
        >
          {narrativesElements}
        </Carousel>
      </React.Fragment>
    );
  };

  const printTimeline = () => {
    const { meetings, speedDatings } = getTimelineCards();

    if (!meetings.length && !speedDatings.length && !scheduleSpeedDatings) {
      return null;
    }

    return (<Agenda meetings={meetings} speedDatings={speedDatings} />);
  };

  const usersCarousel = useMemo(() => {
    if (!thereAreUsers) {
      return null;
    }

    return (
      <React.Fragment>
        <div className='header_with_searcher'>
          <div className='title_section' dangerouslySetInnerHTML={{ __html: literals.recommendedUsers }} />
          <input className='form_input_stl' value={searchUsers} onChange={handleSearchUsers} placeholder={literals.search} />
        </div>
        {recommendedUsers.length ? (
          <div style={{ opacity: isLoadingUsers ? 0.5 : 1, pointerEvents: isLoadingUsers ? 'none' : 'all' }}>
            <SimpleCarousel classesList='user_wrapper' showButtons>
              {recommendedUsers.map((elem) => {
                return (
                  <UserSmallCard key={elem.id} user={elem} onClick={handleClickUser} />
                );
              })}
            </SimpleCarousel>
          </div>
        ) : (
          <NoElements style={{ opacity: isLoadingUsers ? 0.5 : 1 }}>
            <div className='nocontent'>
              <img src={NoResultsImg} alt='' />
              <div className='text'>{literals.noUsers}</div>
            </div>
          </NoElements>
        )}
      </React.Fragment>
    );
  }, [recommendedUsers, searchUsers, isLoadingUsers]);

  const tablesCarousel = useMemo(() => {
    if (!thereAreTables) {
      return null;
    }

    return (
      <React.Fragment>
        <div className='header_with_searcher'>
          <div className='title_section' dangerouslySetInnerHTML={{ __html: literals.recommendedTables }} />
          <input className='form_input_stl' value={searchTables} onChange={handleSearchTables} placeholder={literals.search} />
        </div>
        {recommendedTables.length ? (
          <div style={{ opacity: isLoadingTables ? 0.5 : 1, pointerEvents: isLoadingUsers ? 'none' : 'all' }}>
            <SimpleCarousel classesList='theme_table_card' showButtons>
              {_.map(recommendedTables, think => (<ThemeTableCard key={think.id} data={think} row onClick={handleClickTable} />))}
            </SimpleCarousel>
          </div>
        ) : (
          <NoElements style={{ opacity: isLoadingTables ? 0.5 : 1 }}>
            <div className='nocontent'>
              <img src={NoResultsImg} alt='' />
              <div className='text'>{literals.noTables}</div>
            </div>
          </NoElements>
        )}
      </React.Fragment>
    );
  }, [recommendedTables, searchTables, isLoadingTables]);

  const printMobileStoreLinks = () => {
    return (
      <div className='phone_content'>
        <PhoneUrl>
          <div className='text' dangerouslySetInnerHTML={{ __html: literals.enjoy }} />
          <div className='photos'>
            <img src={Apple} alt='' onClick={openApple} />
            <img src={Google} alt='' onClick={openGoogle} />
          </div>
        </PhoneUrl>
      </div>
    );
  };

  const usersSwipeableCards = useMemo(() => {
    if (!selectedUser) {
      return null;
    }

    return (
      <UsersModal
        usersIds={_.map(recommendedUsers, user => user.id)}
        userSelected={selectedUser}
        onEnd={removeSelectedUser}
      />
    );
  }, [selectedUser]);

  if (isLoading) {
    return (
      <LoadingWrapper>
        <LoadingComponent colorAnimation={app.color1} />
      </LoadingWrapper>
    );
  }

  return (
    <NarrativesWrapper className='main_padding_desktop'>
      <div>
        {printCall4Impact()}
        {printTimeline()}
        {usersCarousel}
        {tablesCarousel}
        {printMobileStoreLinks()}
        {usersSwipeableCards}
      </div>
    </NarrativesWrapper>
  );
}

Narratives.propTypes = {
  narratives: PropTypes.array,
  themeTables: PropTypes.array,
  literals: PropTypes.object.isRequired,
  app: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  resetAll: PropTypes.func.isRequired,
  setBreadcrumb: PropTypes.func.isRequired,
  resetContext: PropTypes.func.isRequired,
  itemsPreferencesFromSerendipity: PropTypes.array,
  scheduleSpeedDatings: PropTypes.array,
};

Narratives.defaultProps = {
  narratives: null,
  themeTables: [],
  scheduleSpeedDatings: [],
  itemsPreferencesFromSerendipity: [],
};

export default Narratives;



// WEBPACK FOOTER //
// ./src/routes/Narratives/components/index.jsx